/**
 * Board rendering helpers. The slide renders EXACTLY as authored (element
 * x/y/w/h, row layouts, authored styles honored verbatim). This module only
 * holds the native canvas dimensions and small shape-only helpers.
 */

import type { CSSProperties } from 'react';

/** Width the admin canvas authors against (px) — fixed 1280-wide landscape. */
export const CANVAS_WIDTH = 1280;
/** Reference height a row's `heightPercent` is taken against (px) — 16:9 base. */
export const SLIDE_REF_HEIGHT = 720;

/**
 * Elements whose frame hugs their rendered content instead of the stored box —
 * a chip IS its pill, so its stored w/h is cosmetic and usually stale. Sizing
 * them to that box clips them. Mirrors `AUTO_SIZE_TYPES` in the admin's
 * `src/lib/freeform.ts`; keep the two in step.
 */
export const AUTO_SIZE_TYPES = new Set(['chip', 'icon']);

const MEDIA_TYPES = new Set(['image', 'video', 'embed', '3d']);

/** Is this element a piece of media (image / video / embed / 3d)? */
export function isMedia(type: string): boolean {
  return MEDIA_TYPES.has(type);
}

/** Parse a raw `customCss` declaration string into a React style object. */
function parseCustomCss(css?: string): CSSProperties {
  if (!css) return {};
  const out: Record<string, string> = {};
  css.split(';').forEach((decl) => {
    const idx = decl.indexOf(':');
    if (idx === -1) return;
    const prop = decl.slice(0, idx).trim();
    const val = decl.slice(idx + 1).trim();
    if (!prop || !val) return;
    const camel = prop.replace(/-([a-z])/g, (_, ch: string) => ch.toUpperCase());
    out[camel] = val;
  });
  return out as CSSProperties;
}

/**
 * Collect additive style fields (border / opacity / boxShadow) plus the raw
 * `customCss` (merged LAST so it wins) into an inline-style object the renderer
 * applies on top of an element's positioning wrapper.
 */
export function styleOverrides(style: Record<string, unknown> | undefined): CSSProperties {
  if (!style) return {};
  const o: Record<string, string | number> = {};
  const bw = style.borderWidth as number | string | undefined;
  const bwNum = typeof bw === 'number' ? bw : Number(bw ?? 0);
  if (bw != null && Number.isFinite(bwNum) && bwNum > 0) {
    o.borderWidth = typeof bw === 'number' ? `${bw}px` : bw;
    // CSS forces border-width to compute to 0 when border-style is `none`, so a
    // width-only authored border (legacy / AI-generated) would be invisible.
    // Default to `solid` — matches the editor's authoredBorder() helper.
    o.borderStyle = typeof style.borderStyle === 'string' ? style.borderStyle : 'solid';
    o.borderColor = typeof style.borderColor === 'string' ? style.borderColor : '#1f2937';
  }
  if (typeof style.opacity === 'number') o.opacity = style.opacity;
  if (typeof style.boxShadow === 'string') o.boxShadow = style.boxShadow;
  return { ...o, ...parseCustomCss(style.customCss as string | undefined) };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Render text exactly as authored. Authored HTML is rendered as-is; plain text
 * has its newlines turned into `<br>` so authored line breaks are preserved.
 */
export function normalizeBoardText(raw: string): string {
  if (raw.trim().startsWith('<')) return raw;
  return `<p>${escapeHtml(raw).replace(/\r\n|\r|\n/g, '<br>')}</p>`;
}

/** A run of text sharing one colour / weight / size — the handwriting renderer
 *  draws each run's glyphs with those, so per-word styling survives handwriting.
 *  `\n` is kept in `text` for block / <br> breaks. Absent fields = element default. */
export interface StyledRun {
  text: string;
  color?: string;
  weight?: number;
  /** font-size in px, from an inline `font-size` mark. */
  size?: number;
}

/** Parse authored HTML into styled runs (colour / bold / size), the styled
 *  equivalent of a plain-text flatten. Reuses `parseCustomCss` for the inline
 *  `<span style>` marks; `<strong>`/`<b>` set weight 700. */
export function htmlToRuns(html: string): StyledRun[] {
  if (typeof document === 'undefined') return [{ text: html.replace(/<[^>]+>/g, '') }];
  const root = document.createElement('div');
  root.innerHTML = html;

  const runs: StyledRun[] = [];
  const push = (text: string, s: StyledRun) => {
    if (!text) return;
    const last = runs[runs.length - 1];
    if (last && last.color === s.color && last.weight === s.weight && last.size === s.size) {
      last.text += text;
    } else {
      runs.push({ text, color: s.color, weight: s.weight, size: s.size });
    }
  };

  const BLOCK = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
  const walk = (node: Node, s: StyledRun) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        push(child.textContent ?? '', s);
        return;
      }
      if (child.nodeType !== 1) return;
      const el = child as HTMLElement;
      const tag = el.tagName;
      if (tag === 'BR') {
        push('\n', s);
        return;
      }
      const next: StyledRun = { text: '', color: s.color, weight: s.weight, size: s.size };
      if (tag === 'STRONG' || tag === 'B') next.weight = 700;
      const css = parseCustomCss(el.getAttribute('style') || undefined) as Record<string, string>;
      if (css.color) next.color = css.color;
      if (css.fontWeight) {
        const w = Number(css.fontWeight);
        if (Number.isFinite(w)) next.weight = w;
      }
      if (css.fontSize) {
        const sz = parseFloat(css.fontSize);
        if (Number.isFinite(sz)) next.size = sz;
      }
      walk(el, next);
      if (BLOCK.has(tag)) push('\n', s);
    });
  };
  walk(root, { text: '' });

  while (runs.length && runs[runs.length - 1].text === '\n') runs.pop();
  return runs.length ? runs : [{ text: '' }];
}
